import React, { useState } from 'react';
import Modal, { ModalProps } from '~/App/Components/Modal';
import { StyleableProps } from '~/App/Components/HOC/Styleable';
import styled from '@emotion/styled';
import theme from '~/utils/colours';
import Input from '../AddMediaEmbedInput';
import Button from '~/App/Components/Button';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { EmbedItem, EmbedType, OEmbed } from '~/types/models';
import EmbedServicesList, {
  UnorderedList,
  IconWrapper,
} from '../EmbedServicesList';
import axios from 'axios';
import PanelSection from '../PanelSection';

const styles: StyleableProps = {
  panel: ({ base }) => ({
    ...base,
    maxWidth: '455px',
    padding: '30px 53px 30px 44px',
  }),
  content: ({ base }) => ({
    ...base,
    maxWidth: '495px',
    marginTop: '190px',
  }),
  overlay: ({ base }) => ({
    ...base,
    padding: '20px',
  }),
};

const Title = styled.h2`
  margin-top: 0;
  font-weight: 700;
  color: ${theme.greyDarkAlt2};
`;

const Body = styled.div`
  p {
    color: ${theme.greyDarkAlt5};
  }
`;

const InputWrapper = styled.div`
  display: flex;

  > div {
    margin-right: 15px;
  }
`;

const ErrorWrapper = styled.div`
  font-size: 12px;
  color: #a94442;
`;

const StyledEmbedServicesList = styled(EmbedServicesList)`
  ${UnorderedList} {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: end;
    justify-content: end;

    li {
      width: 20%;
    }

    ${IconWrapper} {
      width: 55px;
      height: 55px;

      img {
        width: 100%;
        max-width: 39px;
        max-height: 39px;
      }
    }

    li.google_slides {
      ${IconWrapper} {
        img {
          width: auto;
        }
      }
    }

    @media (max-width: 1024px) {
      li {
        width: 25%;
      }
    }

    @media (max-width: 768px) {
      li {
        width: 20%;
      }
    }

    @media (max-width: 480px) {
      li {
        width: 33.3333%;
      }
    }
  }
`;

const AddButton = styled(Button)`
  svg {
    margin-right: 0 !important;
  }
`;

export interface AddMediaEmbedProps extends ModalProps {
  onEmbedAdded: (item: EmbedItem) => void;
}

const AddMediaEmbed = ({ onEmbedAdded, ...modalProps }: AddMediaEmbedProps) => {
  const [embedValue, setEmbedValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const getOEmbedData = async (content_url) => {
    setIsLoading(true);
    try {
      const res = await axios.post('/api/v3/message-templates/verify-oembed', {
        content_url,
      });
      setIsLoading(false);
      return { valid: true, data: res.data.data };
    } catch (error) {
      setIsLoading(false);
      return { valid: false, data: error.response.data.error };
    }
  };

  const handleAddEmbedItem = async () => {
    setErrorMsg('');
    const result = await getOEmbedData(embedValue);
    const oEmbed = result.data as OEmbed;

    if (result.valid) {
      const item: EmbedItem = {
        type: EmbedType.OEmbed,
        data: oEmbed,
        content_url: embedValue,
        icon: (oEmbed.icon as string) || '',
        title: oEmbed.provider_name as string,
      };

      onEmbedAdded(item);
    } else {
      setErrorMsg(result.data.message);
    }
  };

  return (
    <Modal {...{ styles, ...modalProps }}>
      <Title>Media Embed</Title>
      <Body>
        <PanelSection>
          <p>
            Paste in a link to display the content block of your choice. This
            will be embedded as an iFrame.
          </p>
          <InputWrapper>
            <Input
              onChange={(event) => setEmbedValue(event.currentTarget.value)}
            />
            <AddButton
              loading={isLoading}
              icon={faPlus}
              bsStyle='primary'
              onClick={handleAddEmbedItem}
            />
          </InputWrapper>
          {errorMsg && <ErrorWrapper>{errorMsg}&nbsp;</ErrorWrapper>}
        </PanelSection>
        <StyledEmbedServicesList />
      </Body>
    </Modal>
  );
};

export default AddMediaEmbed;
